import { useEffect, useRef, useState } from "react";
import { Subscription } from "rxjs";

/**
 * Đồng bộ nền với server: cứ `interval` ms gọi `run` (request nên dùng { silent: true } để không hiện loader / toast).
 * Chỉ chạy khi `enabled` (vd còn máy đang chạy), tạm dừng khi tab trình duyệt ẩn, gọi lại ngay khi tab hiện lại.
 * Hẹn lần sau ngay khi gửi request (ở dev lỗi trả NEVER nên không đợi kết quả), huỷ request cũ nếu còn treo.
 * `run` nên chỉ setState khi dữ liệu thật sự đổi để không nháy giao diện.
 */
export function useSilentSync(run: () => Subscription | Subscription[] | void, enabled: boolean, interval = 60000) {
  const runRef = useRef(run);
  const enabledRef = useRef(enabled);

  runRef.current = run;
  enabledRef.current = enabled;

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let subs: Subscription[] = [];

    const schedule = () => {
      if (!stopped) timer = setTimeout(tick, interval);
    };

    function tick() {
      if (stopped) return;

      if (document.hidden || !enabledRef.current) {
        schedule();

        return;
      }

      subs.forEach((sub) => sub.unsubscribe());
      schedule();

      const result = runRef.current();

      subs = result ? ([] as Subscription[]).concat(result) : [];
    }

    const onVisible = () => {
      if (!document.hidden) {
        clearTimeout(timer);
        tick();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      clearTimeout(timer);
      subs.forEach((sub) => sub.unsubscribe());
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [interval]);
}

/**
 * Giờ hiện tại (ms) tự cập nhật mỗi `every` ms và đúng lúc mốc gần nhất trong `deadlines` trôi qua,
 * để giao diện đổi trạng thái (hết giờ, hết hạn) ngay mà không cần tải lại.
 */
export function useNow(deadlines: number[], every = 30000) {
  const [now, setNow] = useState(() => Date.now());
  const key = deadlines.join(",");

  useEffect(() => {
    const future = deadlines.filter((time) => time > now);
    const next = future.length ? Math.min(...future) - Date.now() + 500 : Infinity;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(1000, Math.min(every, next)));

    return () => clearTimeout(timer);
  }, [key, now, every]);

  return now;
}
